/**
 * Qualified Lead Detail page (`/admin/ghl-qualifier/qualified/:id`).
 *
 * Single-row view of one `ghl_lead_qualifications` record, joined with the
 * matching `ghl_conversation_analyses` row written by the qualifier worker
 * and the contact metadata cached in `ghl_contacts_cache`.
 */

import { useNavigate, useParams, useLocation } from 'react-router-dom'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import { Skeleton } from '@/components/ui/skeleton'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog'
import { ArrowLeft, ExternalLink, RefreshCcw, XCircle } from 'lucide-react'
import { toast } from 'sonner'
import {
  buildGHLContactUrl,
  formatConfidence,
  formatRelativeTime,
  getConfidenceColorClass,
  getQualifierOutcomeColor,
  getQualifierPatternColor,
} from '@/lib/ghl-qualifier'
import {
  useCancelQualifiedLead,
  useQualifiedLeadDetail,
  useQualifierGoogleAdsSettings,
  useRetryQualifiedLead,
} from '@/hooks/useGHLQualifierDashboard'
import { EvidenceQuotePanel } from '@/components/ghl-qualifier/EvidenceQuotePanel'
import { ConversationTranscript } from '@/components/ghl-qualifier/ConversationTranscript'
import { ClaudeAnalysisPanel } from '@/components/ghl-qualifier/ClaudeAnalysisPanel'
import { TagTimelinePanel } from '@/components/ghl-qualifier/TagTimelinePanel'
import { GoogleAdsUploadDetail } from '@/components/ghl-qualifier/GoogleAdsUploadDetail'
import { FormCallCheckDetail } from '@/components/ghl-qualifier/FormCallCheckDetail'

export default function QualifiedLeadDetailPage() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const location = useLocation()
  const detail = useQualifiedLeadDetail(id)
  const cancelMutation = useCancelQualifiedLead()
  const retryMutation = useRetryQualifiedLead()
  // Hoisted to keep hook order stable across renders. Args are passed
  // conditionally — the actual fetch is gated on the locationId being known.
  const gaSettings = useQualifierGoogleAdsSettings(
    detail.data?.qualification.location_id ?? '',
    !!detail.data?.qualification.location_id
  )

  const back = () => {
    if (detail.data?.qualification.location_id) {
      navigate({
        pathname: `/admin/ghl-qualifier/location/${detail.data.qualification.location_id}/qualified-leads`,
        search: location.search,
      })
    } else {
      navigate({ pathname: '/admin/ghl-qualifier', search: location.search })
    }
  }

  if (detail.isLoading) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-8 w-32" />
        <Skeleton className="h-24 w-full" />
        <div className="grid gap-4 lg:grid-cols-2">
          <Skeleton className="h-48 w-full" />
          <Skeleton className="h-48 w-full" />
        </div>
        <Skeleton className="h-96 w-full" />
      </div>
    )
  }

  if (!detail.data) {
    return (
      <div className="space-y-6">
        <Button variant="ghost" size="sm" onClick={back} className="gap-2">
          <ArrowLeft className="h-4 w-4" />
          Back
        </Button>
        <Card>
          <CardContent className="py-12 text-center text-muted-foreground">
            <p className="text-lg font-medium">Qualification record not found.</p>
            <p className="text-sm mt-1">
              It may have been deleted or you may not have access.
            </p>
          </CardContent>
        </Card>
      </div>
    )
  }

  const { qualification, analysis, contact } = detail.data
  const outcome = getQualifierOutcomeColor(qualification.status, qualification.qualification_result)
  const pattern = getQualifierPatternColor(qualification.classification_pattern)
  const directMode =
    gaSettings.data?.conversion_delivery_method === 'google_ads'

  const displayName =
    contact?.full_name ?? contact?.first_name ?? qualification.contact_id
  const email = contact?.email ?? null
  const phone = contact?.phone ?? null

  const ghlUrl = buildGHLContactUrl(qualification.location_id, qualification.contact_id)
  const isCancellable = ['pending', 'processing', 'scheduled'].includes(qualification.status)
  const isRetryable = qualification.status === 'failed'

  return (
    <div className="space-y-6">
      {/* Back button + Rerun/Cancel actions */}
      <div className="flex items-center justify-between">
        <Button variant="ghost" size="sm" onClick={back} className="gap-2">
          <ArrowLeft className="h-4 w-4" />
          Back to feed
        </Button>
        {isRetryable && (
          <AlertDialog>
            <AlertDialogTrigger asChild>
              <Button variant="outline" size="sm" className="gap-2" disabled={retryMutation.isPending}>
                <RefreshCcw className={`h-4 w-4 ${retryMutation.isPending ? 'animate-spin' : ''}`} />
                {retryMutation.isPending ? 'Rerunning…' : 'Rerun check'}
              </Button>
            </AlertDialogTrigger>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>Rerun this qualified-lead check?</AlertDialogTitle>
                <AlertDialogDescription>
                  The check will be re-queued and processed immediately. A new Claude
                  analysis runs against the current conversation and tags are re-applied
                  (re-applying an existing tag is a no-op in GHL).
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel>Keep as failed</AlertDialogCancel>
                <AlertDialogAction
                  onClick={async () => {
                    try {
                      const result = await retryMutation.mutateAsync({ id: qualification.id })
                      if (result.immediate) {
                        toast.success('Rerun complete — result updated')
                      } else {
                        toast.success('Check re-queued — it will process within ~2 minutes')
                      }
                    } catch (err) {
                      toast.error(
                        err instanceof Error ? err.message : 'Failed to rerun check'
                      )
                    }
                  }}
                >
                  Rerun check
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        )}
        {isCancellable && (
          <AlertDialog>
            <AlertDialogTrigger asChild>
              <Button variant="outline" size="sm" className="gap-2 text-destructive hover:text-destructive">
                <XCircle className="h-4 w-4" />
                Cancel check
              </Button>
            </AlertDialogTrigger>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>Cancel this qualified-lead check?</AlertDialogTitle>
                <AlertDialogDescription>
                  This contact will not be evaluated by the Qualified Lead Job. No tags will
                  be applied and no Claude call will fire. This cannot be undone — to qualify
                  this contact later you'd need to re-trigger the flow manually.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel>Keep check</AlertDialogCancel>
                <AlertDialogAction
                  onClick={async () => {
                    try {
                      await cancelMutation.mutateAsync({ id: qualification.id })
                      toast.success('Qualified-lead check cancelled')
                    } catch (err) {
                      toast.error(
                        err instanceof Error ? err.message : 'Failed to cancel check'
                      )
                    }
                  }}
                  className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                >
                  Cancel check
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        )}
      </div>

      {/* Contact identity */}
      <div>
        <h1 className="text-2xl font-bold">{displayName}</h1>
        <p className="text-sm text-muted-foreground mt-1">
          {[email, phone, `Contact ${qualification.contact_id}`]
            .filter(Boolean)
            .join('  ·  ')}
        </p>
        <p className="text-xs text-muted-foreground mt-0.5">
          Created {new Date(qualification.created_at).toLocaleString()}
          {' · '}
          Location: <span className="font-mono">{qualification.location_id}</span>
        </p>
      </div>

      {/* Header card */}
      <Card>
        <CardContent className="pt-6 space-y-3">
          <div className="flex flex-wrap items-center gap-3">
            <Badge className={`${outcome.bg} ${outcome.text} hover:${outcome.bg} text-sm`}>
              {outcome.label}
            </Badge>
            {qualification.classification_pattern && (
              <Badge className={`${pattern.bg} ${pattern.text} hover:${pattern.bg}`}>
                Pattern: {pattern.label}
              </Badge>
            )}
            <span
              className={`text-sm font-medium ${getConfidenceColorClass(qualification.confidence)}`}
            >
              Confidence: {formatConfidence(qualification.confidence)}
            </span>
          </div>
          <dl className="grid grid-cols-[max-content_1fr] gap-x-4 gap-y-1 text-sm">
            {qualification.tags_applied && qualification.tags_applied.length > 0 ? (
              <>
                <dt className="text-muted-foreground">Tags applied</dt>
                <dd>
                  <span className="font-mono text-xs">
                    {qualification.tags_applied.join(', ')}
                  </span>
                  <span className="ml-2 text-muted-foreground text-xs">
                    {formatRelativeTime(qualification.updated_at)}
                  </span>
                </dd>
              </>
            ) : null}
            {qualification.input_tokens != null || qualification.output_tokens != null ? (
              <>
                <dt className="text-muted-foreground">Tokens</dt>
                <dd className="text-sm tabular-nums">
                  {(qualification.input_tokens ?? 0).toLocaleString()} in /{' '}
                  {(qualification.output_tokens ?? 0).toLocaleString()} out
                  {qualification.model_used ? (
                    <span className="ml-2 text-muted-foreground text-xs">
                      · {qualification.model_used}
                    </span>
                  ) : null}
                </dd>
              </>
            ) : null}
            <dt className="text-muted-foreground">Status</dt>
            <dd className="font-mono text-xs">{qualification.status}</dd>
          </dl>
          {qualification.error_message ? (
            <div className="rounded-md bg-red-50 border border-red-200 px-3 py-2 text-xs text-red-900">
              <strong>Error:</strong> {qualification.error_message}
            </div>
          ) : null}
        </CardContent>
      </Card>

      {/* Pending state — hide deep panels until the job has run */}
      {qualification.status === 'scheduled' || qualification.status === 'pending' ? (
        <Card>
          <CardContent className="py-10 text-center text-muted-foreground">
            <p className="text-base font-medium">Job is scheduled</p>
            <p className="text-sm mt-1">
              The qualifier will run on{' '}
              <time dateTime={qualification.fire_at}>
                {new Date(qualification.fire_at).toLocaleString()}
              </time>
              . Refresh after that time to see the analysis.
            </p>
          </CardContent>
        </Card>
      ) : (
        <>
          <div className="grid gap-4 lg:grid-cols-2">
            <EvidenceQuotePanel
              evidenceQuote={qualification.evidence_quote}
              clientBookingQuote={qualification.client_booking_quote}
              contactName={displayName}
              capturedAt={qualification.created_at}
            />
            <ClaudeAnalysisPanel
              classificationPattern={qualification.classification_pattern}
              qualificationResult={qualification.qualification_result}
              reasoning={qualification.qualification_reason}
              rawClaudeResponse={analysis?.raw_claude_response ?? null}
            />
          </div>


          <ConversationTranscript rawMessages={analysis?.raw_messages ?? null} />

          <TagTimelinePanel
            tags={qualification.tags_applied}
            appliedAt={qualification.updated_at}
          />
        </>
      )}

      {/* Google Ads + form/phone-call cards are INDEPENDENT of the qualified
          job's lifecycle: the form/phone check fires ~1h after contact
          creation, while the qualified analysis runs at +24h. Keeping these
          inside the pending-gated branch above hid real upload data during
          that ~23h window. They self-gate on google_ads mode (`enabled`) and
          render their own empty/data states, so show them regardless of the
          qualified job's status. */}
      <GoogleAdsUploadDetail
        contactId={qualification.contact_id}
        eventType="qualified"
        enabled={directMode}
      />

      <FormCallCheckDetail contactId={qualification.contact_id} enabled={directMode} />

      <GoogleAdsUploadDetail
        contactId={qualification.contact_id}
        eventType="form_submit"
        enabled={directMode}
      />

      <GoogleAdsUploadDetail
        contactId={qualification.contact_id}
        eventType="phone_call"
        enabled={directMode}
      />

      {/* Footer actions */}
      <div className="flex flex-wrap gap-2 pt-2">
        <Button asChild variant="outline" size="sm" className="gap-2">
          <a href={ghlUrl} target="_blank" rel="noopener noreferrer">
            View in GHL
            <ExternalLink className="h-3.5 w-3.5" />
          </a>
        </Button>
      </div>
    </div>
  )
}
